import _ from "lodash";
import { getTypeOfInstance } from "./validation";

// Build valid placeholders for URL when creating or updating a webhook
const buildUrlPlaceholders = (
  url: string,
  placeholderJSON: any,
  delimiter = "{}",
) => {
  const numUrlPlaceholders = url.split(delimiter).length - 1;
  const urlPlaceholders: any = {};
  if (placeholderJSON !== null) {
    const keysToIterate = Object.keys(placeholderJSON);
    for (let i = 0; i < keysToIterate.length; i += 1) {
      if (i === numUrlPlaceholders) {
        break;
      }
      const key = keysToIterate[i];
      const val = placeholderJSON[key];

      // Update value of key at index in object
      if (!(key.toString() in urlPlaceholders)) {
        urlPlaceholders[key.toString()] = null;
      }
      urlPlaceholders[key.toString()] = val;
    }
  }
  return urlPlaceholders;
};

const addtoObject = (
  formBody: any,
  objToSubmit: any,
  idx: any,
  key: any,
  val: string,
  delimiter: string = "{}",
) => {
  const dummy = objToSubmit;
  const chosenKey = key[0];

  // Check number of allowed indices
  const numBodyPlaceholders = formBody[chosenKey].split(delimiter).length - 1;

  // If index is not allowed due to placeholder being removed, break
  if (idx === numBodyPlaceholders.toString()) {
    return dummy;
  }

  // Update value of key at index in object
  if (!(chosenKey.toString() in dummy)) {
    dummy[chosenKey.toString()] = {};
  }

  // Otherwise, add it to the object at index
  if (!(idx.toString() in dummy[chosenKey.toString()])) {
    dummy[chosenKey.toString()][idx.toString()] = null;
  }
  dummy[chosenKey.toString()][idx.toString()] = val;
  return dummy;
};

const addtoObjectDeep = (
  formBody: any,
  objToSubmit: any,
  idx: any,
  keys: any[],
  val: string,
  delimiter: string = "{}",
) => {
  const dummy = objToSubmit;

  // Get value of nested key that needs requires placeholders
  let candidate = null;
  for (let i = 0; i < keys.length; i += 1) {
    if (!candidate) {
      candidate = formBody[keys[i]];
    } else {
      if (keys[i] in candidate) {
        candidate = candidate[keys[i]];
        // eslint-disable-next-line no-continue
        continue;
      }
      candidate = null;
    }
  }
  if (candidate === null) {
    return dummy;
  }

  const numBodyPlaceholders = candidate.split(delimiter).length - 1;

  // If index is not allowed due to placeholder being removed, break
  if (idx === numBodyPlaceholders.toString()) {
    return dummy;
  }

  // Create empty nested structure with correct keys at correct levels
  candidate = {};

  const keysReverse = keys.reverse();
  for (let i = 0; i < keysReverse.length; i += 1) {
    // Populate dummy in reverse order
    const reverseKey = keysReverse[i];

    if (i === 0) {
      if (!(reverseKey in dummy)) {
        const dummyObj: any = {};
        dummyObj[reverseKey.toString()] = {};
        if (!(idx.toString() in dummyObj[reverseKey.toString()])) {
          dummyObj[reverseKey.toString()][idx.toString()] = null;
        }
        dummyObj[reverseKey.toString()][idx.toString()] = val;
        candidate = JSON.parse(JSON.stringify(dummyObj));
      }
    } else {
      dummy[reverseKey.toString()] = candidate;
      candidate = JSON.parse(JSON.stringify(dummy));
    }
  }

  return JSON.parse(JSON.stringify(dummy));
};

const getIndexAndKeys = (key: any, nested = false) => {
  // Split key into index and key
  const splitKey = key.split("_");
  const [idx, newKey] = splitKey;

  // If not nested, return key
  if (!nested) {
    return [idx, [newKey]];
  }

  // Otherwise, split by "."
  const newKey2 = newKey.split(".");
  return [idx, newKey2];
};

const getKeys = (obj: { [key: string]: any }) => Object.keys(obj);

const combineNestedObjects = (obj: { [key: string]: any }, src: any) => {
  const newObject = _.merge(obj, src);
  return newObject;
};

// Creates the JSON object for submission from the form data
const createObjectForSubmission = (obj: any, formBody: any) => {
  let objectForSubmission = {};

  // Get root keys of object
  const rootKeys = Object.keys(obj);

  // Iterate through root keys and process data
  const check = [];
  for (let i = 0; i < rootKeys.length; i += 1) {
    // Get key and value
    const currentRootKey = rootKeys[i];
    const currentRootVal = obj[currentRootKey];

    // Check if root key has a ".", if so, then it is nested
    const nestedCheck = currentRootKey.includes(".");

    // If it is not nested,
    if (!nestedCheck) {
      const [idx, cleanKey] = getIndexAndKeys(currentRootKey, nestedCheck);
      if (cleanKey[0] in formBody) {
        objectForSubmission = addtoObject(
          formBody,
          objectForSubmission,
          idx,
          cleanKey,
          currentRootVal,
        );
      }
    } else {
      // If it is nested
      const [nestedIdx, nestedKeys] = getIndexAndKeys(
        currentRootKey,
        nestedCheck,
      );

      if (nestedKeys[0] in formBody) {
        // Add to the object for submission at the lowest level of nesting
        const objectForCheck = addtoObjectDeep(
          formBody,
          objectForSubmission,
          nestedIdx,
          nestedKeys,
          currentRootVal,
        );
        check.push(objectForCheck);
      }
    }
  }

  if (check.length > 1) {
    let newAnswer = null;
    for (let i = 0; i < check.length - 1; i += 1) {
      if (!newAnswer) {
        newAnswer = combineNestedObjects(check[i], check[i + 1]);
      } else {
        newAnswer = combineNestedObjects(newAnswer, check[i + 1]);
      }
    }
    return newAnswer;
  }
  return objectForSubmission;
};

// Build valid placeholders for body when creating or updating a webhook
const buildBodyPlaceholders = (formObj: any, formBody: any) => {
  const placeholders = createObjectForSubmission(formObj, formBody);
  return placeholders;
};

const checkIfObjectIsNested = (obj: { [key: string]: any }) => {
  const keys = getKeys(obj);
  for (let i = 0; i < keys.length; i += 1) {
    const currentVal = obj[keys[i]];
    if (getTypeOfInstance(currentVal) === "object") {
      return true;
    }
  }
  return false;
};

const formatBodyForForm = (body: any) => {
  const newBodyPlaceholder: any = {};
  if (!body) {
    return newBodyPlaceholder;
  }

  const bodyPlaceholderKeys = getKeys(body);
  for (let i = 0; i < bodyPlaceholderKeys.length; i += 1) {
    const objKey = bodyPlaceholderKeys[i];
    const currentVal = body[objKey];
    const currentValKeys = getKeys(currentVal);

    // If not nested
    const nestedCheck = checkIfObjectIsNested(currentVal);
    if (!nestedCheck) {
      for (let j = 0; j < currentValKeys.length; j += 1) {
        const chosenKey = `${j}_${objKey}`;
        newBodyPlaceholder[chosenKey] = body[objKey][j];
      }
    } else {
      let keyString;
      let val;
      for (let j = 0; j < currentValKeys.length; j += 1) {
        const nestedVal = currentVal[currentValKeys[j]];

        if (!checkIfObjectIsNested(nestedVal)) {
          keyString = `${objKey}.${currentValKeys[j]}`;
          val = nestedVal;

          const valKeys = getKeys(nestedVal);
          for (let m = 0; m < valKeys.length; m += 1) {
            const chosenKey = `${m}_${keyString}`;
            newBodyPlaceholder[chosenKey] = val[valKeys[m]];
          }
        }
      }
    }
  }
  return newBodyPlaceholder;
};

const buildBodyPlaceholderLookup = (body: any, placeholder = "{{}}") => {
  let parsedBodyValid = true;
  const lookup: any[] = [];
  try {
    const parsedBody = JSON.parse(body);
    const parsedBodyKeys = getKeys(parsedBody);
    for (let i = 0; i < parsedBodyKeys.length; i += 1) {
      const val = parsedBody[parsedBodyKeys[i]];

      // If object is a string
      const objectType = getTypeOfInstance(val);
      if (objectType === "string") {
        if (val.includes(placeholder)) {
          const numPlaceholders = val.split(placeholder).length - 1;
          const addPlaceholder = [parsedBodyKeys[i], numPlaceholders];
          if (!lookup.includes(addPlaceholder)) {
            lookup.push(addPlaceholder);
          }
        }
      } else if (objectType === "array") {
        // If object is an array
        // Loop through each element of array
        for (let j = 0; j < val.length; j += 1) {
          const arrayVal = val[j];
          if (arrayVal.includes(placeholder)) {
            const numPlaceholders = arrayVal.split(placeholder).length - 1;
            const addPlaceholder = [parsedBodyKeys[i], numPlaceholders];
            if (!lookup.includes(addPlaceholder)) {
              lookup.push(addPlaceholder);
            }
          }
        }
      } else if (objectType === "object") {
        // Loop through each key in the object
        const objectKeys = getKeys(val);
        for (let j = 0; j < objectKeys.length; j += 1) {
          const objectKey = objectKeys[j];
          const objectVal = val[objectKey];
          if (objectVal.includes(placeholder)) {
            const numPlaceholders = objectVal.split(placeholder).length - 1;
            const addPlaceholder = [
              `${parsedBodyKeys[i]}.${objectKey}`,
              numPlaceholders,
            ];
            if (!lookup.includes(addPlaceholder)) {
              lookup.push(addPlaceholder);
            }
          }
        }
      }
    }
  } catch (err) {
    parsedBodyValid = false;
  }
  return { parsedBodyValid, lookup };
};

const checkIfStringIsValidJson = (str: string) => {
  try {
    JSON.parse(str);
    return true;
  } catch (err) {
    return false;
  }
}

export {
  buildUrlPlaceholders,
  buildBodyPlaceholders,
  formatBodyForForm,
  buildBodyPlaceholderLookup,
  checkIfStringIsValidJson
};

// TODO:
// test = {"0_reporter.email.hello": 1,}
// result = {"report": {"email": {"hello": {"0": 1}}}
